import React, { FC, useEffect, useState } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import {
  Button,
  Menu,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Paper,
  Typography,
  ListItemIcon,
  ListItemText,
  Stack,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useExportAbsenceData } from "src/Pages/Company/Absences/api/useExportAbsenceData";
import { getYearRange } from "src/utils/Company/Absences/checkExcludedWeekDays";
import {
  ExportMediaType,
  ExportType,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

enum EmployeeStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  ALL = "ALL",
}

interface IExportFormProps {
  exportType: ExportType;
  hasYearsFilter: boolean;
}

interface FormData {
  employeeStatus: EmployeeStatus;
  years?: string[];
}

const ExportForm: FC<IExportFormProps> = ({ exportType, hasYearsFilter }) => {
  const companyId = useCompanyId();
  const currentYear = new Date().getFullYear().toString();
  const availableYearOptions = getYearRange(currentYear, 4, 4);
  const [isProcessing, setIsProcessing] = useState(false);
  const formMethods = useForm<FormData>({
    defaultValues: {
      employeeStatus: EmployeeStatus.ACTIVE,
      ...(hasYearsFilter && { years: [currentYear] }),
    },
  });

  const { control } = formMethods;

  const { exportDataMutation, hasDownloadSucceeded } = useExportAbsenceData();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (hasDownloadSucceeded) {
      setIsProcessing(false);
    }
  }, [hasDownloadSucceeded]);

  const handleExport = (exportMediaType: ExportMediaType) => {
    handleClose();
    setIsProcessing(true);

    const formValues = formMethods.getValues();

    let isActive: boolean | null;

    switch (formValues.employeeStatus) {
      case EmployeeStatus.ACTIVE:
        isActive = true;
        break;
      case EmployeeStatus.INACTIVE:
        isActive = false;
        break;
      case EmployeeStatus.ALL:
      default:
        isActive = null;
    }

    exportDataMutation({
      exportMediaType,
      exportType,
      companyId,
      isActive,
      years: hasYearsFilter ? formValues.years : undefined,
    });
  };

  return (
    <Paper sx={{ p: "2rem" }} data-testid="absences-export-form">
      {isProcessing && (
        <Paper
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            p: "1.5rem",
            zIndex: 1,
          }}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2">
              {__("Trwa generowanie raportu")}
            </Typography>
            <CircularProgress />
          </Stack>
        </Paper>
      )}
      <Stack direction="row" justifyContent="space-between">
        <Typography component="h2" variant="h6">
          {exportType === ExportType.CALENDARS
            ? __("Eksport kalendarzy pracowników")
            : __("Eksport wzorców i wymiarów pracowników")}
        </Typography>
        <Button
          variant="contained"
          onClick={handleClick}
          startIcon={<FileDownloadOutlinedIcon />}
          data-testid="export-button"
        >
          {__("Eksportuj")}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          data-testid="export-menu"
        >
          <MenuItem
            onClick={() => {
              handleExport(ExportMediaType.CSV);
            }}
            data-testid="export-csv-option"
          >
            <ListItemIcon>
              <DownloadIcon />
            </ListItemIcon>
            <ListItemText>{__("Eksportuj CSV")}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleExport(ExportMediaType.XLSX);
            }}
            data-testid="export-xlsx-option"
          >
            <ListItemIcon>
              <DownloadIcon />
            </ListItemIcon>
            <ListItemText>{__("Eksportuj XLSX")}</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
      <FormProvider {...formMethods}>
        <form>
          <FormControl component="fieldset" margin="normal">
            <FormLabel>
              <Typography
                component="span"
                variant="subtitle1"
                color="textPrimary"
              >
                {__("Status użytkowników do eksportu")}
              </Typography>
            </FormLabel>
            <Controller
              name="employeeStatus"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  data-testid="absences-export-form__employee-status"
                >
                  <FormControlLabel
                    value={EmployeeStatus.ACTIVE}
                    control={<Radio />}
                    label={__("Aktywni")}
                  />
                  <FormControlLabel
                    value={EmployeeStatus.INACTIVE}
                    control={<Radio />}
                    label={__("Nieaktywni")}
                  />
                  <FormControlLabel
                    value={EmployeeStatus.ALL}
                    control={<Radio />}
                    label={__("Wszyscy")}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
          {hasYearsFilter && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="years-select-label">
                {__("Wybierz lata")}
              </InputLabel>
              <Controller
                name="years"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="years-select-label"
                    multiple
                    input={<OutlinedInput label={__("Wybierz lata")} />}
                    renderValue={(selected) => selected.join(", ")}
                    data-testid="absences-export-form__years"
                    {...field}
                  >
                    {availableYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        <Checkbox
                          checked={(field.value || []).indexOf(year) > -1}
                        />
                        <ListItemText primary={year} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          )}
        </form>
      </FormProvider>
    </Paper>
  );
};

export default ExportForm;
